
import { getPermanentTemplates } from '@/api';
import Button from '@/components/UI/button/Button.vue';
import * as routeNames from '@/router/routeNames';
import * as Sentry from '@sentry/vue';

import { PermanentTemplate } from '@/models/permanentJob';
import { format, parseISO } from 'date-fns';
import { Component, Vue } from 'vue-property-decorator';
import { notificationErrorText } from '@/utils/PortalConstants';

@Component({
  components: {
    Button,
  },
})
export default class PermanentJobsList extends Vue {
  private routeNames = routeNames;
  private loading = false;
  private templates: PermanentTemplate[] = [];
  private totalCount = 0;
  private customerCodeFilter = '';

  private pagination = {
    page: 1,
    perPage: 20,
    totalPages: 0,
  };

  private perPageOptions = [10, 20, 50, 100];

  private fetchToken = 0;

  private tableHeaders = [
    { text: 'ID', value: 'id', width: '70px' },
    { text: 'Customer', value: 'customer_code', width: '120px' },
    { text: 'Caller', value: 'caller' },
    { text: 'Reference', value: 'reference_1' },
    { text: 'Days', value: 'days', sortable: false, width: '180px' },
    { text: 'Start', value: 'start_on', width: '110px' },
    { text: 'Finish', value: 'finish_on', width: '110px' },
    { text: 'Times', value: 'no_of_times', width: '90px' },
    { text: 'Driver Option', value: 'auto_driver_option', width: '130px' },
    { text: '', value: 'actions', sortable: false, width: '50px' },
  ];

  async mounted() {
    await this.fetchTemplates();
  }

  private async fetchTemplates() {
    const token = ++this.fetchToken;
    this.loading = true;
    try {
      const response = await getPermanentTemplates(
        this.pagination.page,
        this.pagination.perPage,
        this.customerCodeFilter || undefined,
      );
      // Drop stale responses so a slower earlier request can't overwrite a newer one.
      if (token !== this.fetchToken) {
        return;
      }
      this.templates = response.results;
      this.totalCount = response.count;
      this.pagination.totalPages = Math.ceil(response.count / this.pagination.perPage);
    } catch (err: any) {
      if (token !== this.fetchToken) {
        return;
      }
      Sentry.captureException(err);
      this.$notify({
        group: 'network',
        text: notificationErrorText,
        type: 'warn',
        duration: 3000,
      });
    } finally {
      if (token === this.fetchToken) {
        this.loading = false;
      }
    }
  }

  private onPageChange() {
    this.fetchTemplates();
  }

  private onPerPageChange() {
    this.pagination.page = 1;
    this.fetchTemplates();
  }

  private onFilterChange() {
    this.pagination.page = 1;
    this.fetchTemplates();
  }

  private onClearFilter() {
    this.customerCodeFilter = '';
    this.pagination.page = 1;
    this.fetchTemplates();
  }

  private formatDate(dateStr: string | null): string {
    if (!dateStr) {
      return '-';
    }
    try {
      return format(parseISO(dateStr), 'dd MMM yy');
    } catch {
      return dateStr;
    }
  }

  private formatDays(item: PermanentTemplate): string {
    const days: string[] = [];
    if (item.required_on_monday) {
      days.push('Mon');
    }
    if (item.required_on_tuesday) {
      days.push('Tue');
    }
    if (item.required_on_wednesday) {
      days.push('Wed');
    }
    if (item.required_on_thursday) {
      days.push('Thu');
    }
    if (item.required_on_friday) {
      days.push('Fri');
    }
    if (item.required_on_saturday) {
      days.push('Sat');
    }
    if (item.required_on_sunday) {
      days.push('Sun');
    }
    return days.join(' ') || 'None';
  }

  private driverOptionLabel(option: string): string {
    const labels: Record<string, string> = {
      prealloc: 'Pre-allocate',
      allocate: 'Allocate',
      complete: 'Complete',
    };
    return labels[option] || option;
  }

  private goToDetail(item: PermanentTemplate) {
    this.$router.push({ name: routeNames.PERMANENT_JOB_DETAIL, params: { id: String(item.id) } });
  }
}
